import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, MapPin, Sparkles, ArrowRight, CalendarDays } from "lucide-react";
import { Button } from "../components/ui/button";
import { events, schoolInfo } from "../mock/mock";

const formatDate = (d) => {
  const date = new Date(d);
  return {
    day: date.getDate(),
    month: date.toLocaleDateString("en-IN", { month: "short" }),
    year: date.getFullYear(),
    full: date.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }),
    weekday: date.toLocaleDateString("en-IN", { weekday: "long" })
  };
};

// Countdown calculator
const useCountdown = (targetDate) => {
  const target = new Date(targetDate).getTime();
  const now = Date.now();
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  return { days, hours };
};

const EventsPage = () => {
  const [filter, setFilter] = useState("upcoming");
  const countdown = useCountdown(schoolInfo.launchDateISO);

  const filtered = useMemo(() => {
    if (filter === "all") return events;
    return events.filter((e) => (filter === "upcoming" ? e.status !== "past" : e.status === "past"));
  }, [filter]);

  return (
    <div>
      <section className="pt-16 pb-12 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-sm border border-orange-100 mb-4">
            <Sparkles className="w-4 h-4 text-orange-600" />
            <span className="text-xs font-semibold text-orange-700 tracking-widest uppercase">Events Calendar</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-stone-800 mb-4">
            Meet Us, <span className="text-orange-600">Grow With Us</span>
          </h1>
          <p className="text-stone-600 max-w-2xl mx-auto">Info sessions, open houses, workshops and milestones on our journey to launching Aadi Gayanam on 1st January 2027.</p>
        </div>
      </section>

      {/* Countdown */}
      <section className="py-10 bg-gradient-to-r from-orange-600 via-red-600 to-amber-600 text-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 max-w-4xl mx-auto">
            <div className="text-center md:text-left">
              <p className="font-devanagari text-lg text-yellow-200 mb-1">आदि गायनम् प्ले स्कूल</p>
              <h3 className="font-display font-bold text-2xl md:text-3xl">Grand Launch Countdown</h3>
              <p className="text-orange-50 text-sm mt-1">1st January 2027, Jaipur</p>
            </div>
            <div className="flex gap-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-6 py-3 border border-white/20 text-center min-w-[100px]">
                <p className="font-display font-bold text-3xl md:text-4xl">{countdown.days}</p>
                <p className="text-xs uppercase tracking-widest text-orange-100">Days</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-6 py-3 border border-white/20 text-center min-w-[100px]">
                <p className="font-display font-bold text-3xl md:text-4xl">{countdown.hours}</p>
                <p className="text-xs uppercase tracking-widest text-orange-100">Hours</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          {/* Filter */}
          <div className="flex justify-center gap-2 mb-10">
            {[
              { key: "upcoming", label: "Upcoming" },
              { key: "past", label: "Past Events" },
              { key: "all", label: "All" }
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-5 py-2 rounded-full text-sm font-semibold border transition-all ${
                  filter === f.key
                    ? "bg-orange-600 text-white border-orange-600"
                    : "bg-white text-stone-700 border-stone-200 hover:border-orange-300"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="space-y-6 max-w-5xl mx-auto">
            {filtered.map((e) => {
              const d = formatDate(e.date);
              const isPast = e.status === "past";
              const isMilestone = e.status === "milestone";
              return (
                <div
                  key={e.id}
                  className={`grid md:grid-cols-[140px_1fr] gap-6 bg-white rounded-3xl border ${isMilestone ? "border-orange-300 shadow-xl" : "border-orange-100 shadow-sm"} p-6 hover:shadow-xl transition-all`}
                >
                  {/* Date block */}
                  <div className={`rounded-2xl p-4 text-center flex flex-col justify-center ${
                    isMilestone ? "bg-gradient-to-br from-orange-600 to-red-600 text-white" :
                    isPast ? "bg-stone-100 text-stone-500" :
                    "bg-gradient-to-br from-orange-100 to-amber-100 text-orange-800"
                  }`}>
                    <p className="text-xs uppercase tracking-widest opacity-70">{d.month}</p>
                    <p className="font-display font-bold text-4xl leading-tight">{d.day}</p>
                    <p className="text-xs opacity-70">{d.year}</p>
                  </div>

                  {/* Details */}
                  <div className="flex flex-col md:flex-row gap-5">
                    <img src={e.image} alt={e.title} className="w-full md:w-40 h-32 md:h-full object-cover rounded-2xl flex-shrink-0" />
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="text-[10px] font-bold text-white bg-orange-600 px-2.5 py-1 rounded-full uppercase tracking-wider">{e.category}</span>
                        {isMilestone && <span className="text-[10px] font-bold text-white bg-red-600 px-2.5 py-1 rounded-full uppercase tracking-wider">Milestone</span>}
                        {isPast && <span className="text-[10px] font-bold text-stone-600 bg-stone-100 px-2.5 py-1 rounded-full uppercase tracking-wider">Past</span>}
                      </div>
                      <h3 className="font-display font-bold text-xl text-stone-800 mb-2">{e.title}</h3>
                      <p className="text-sm text-stone-600 mb-3 leading-relaxed">{e.description}</p>
                      <div className="flex flex-wrap gap-4 text-xs text-stone-500">
                        <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-orange-600" /> {d.weekday}, {d.full}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-orange-600" /> {e.time}</span>
                        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-orange-600" /> {e.location}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="text-center py-16">
                <CalendarDays className="w-12 h-12 text-orange-300 mx-auto mb-3" />
                <p className="text-stone-500">No events to show.</p>
              </div>
            )}
          </div>

          <div className="text-center mt-12">
            <Link to="/admission">
              <Button size="lg" className="bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-full px-8 h-12 btn-shine">
                Register for an Event <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EventsPage;
